import { useEffect } from "react";
import { create } from "zustand";
import { toast } from "@/shared/ui/toast";
import { goAvailable, goOffline } from "./softphoneController";
import { useSoftphoneStore, type MicPermission } from "./softphoneStore";

/**
 * Guarda do microfone (QA): sem microfone o agente NÃO pode ficar "Disponível" — senão o Asterisk
 * continua entregando chamadas da fila que falham (480) porque o navegador não consegue abrir o áudio.
 *
 * Regras (só na aba dona do softphone):
 *  - ao entrar em ONLINE (boot, clique, retorno de pausa) com a permissão já concedida, o microfone é
 *    aberto por um instante com o dispositivo escolhido (`probeMic`) para confirmar que funciona;
 *  - permissão negada (Permissions API ou `getusermediafailed` do JsSIP), microfone ausente/ocupado
 *    ou desconectado (`devicechange`) → `problem` fica preenchido e, se o agente estiver ONLINE,
 *    ele vai para OFFLINE ("Sem microfone") pelo `goOffline` do controller — com chamada em curso,
 *    espera a chamada terminar;
 *  - o botão "Disponível" da UI usa `goAvailableChecked`: confere o microfone antes (o clique é gesto
 *    do usuário, então o navegador pode mostrar o pedido de permissão) e só então chama `goAvailable`;
 *  - "Tentar de novo" (`retryMic`) confere de novo e, se o agente foi tirado do ar por isso, devolve
 *    a "Disponível".
 * Nada aqui muda o motor SIP, o heartbeat ou as regras de pausa: só usa a API pública do controller.
 */

export type MicProblem = "denied" | "notfound" | "busy" | "unsupported" | "error";

/** `sessionInfo` do OFFLINE gravado quando o agente sai do ar por falta de microfone. */
export const MIC_OFFLINE_REASON = "Sem microfone";

export const MIC_PROBLEM_TEXT: Record<MicProblem, { title: string; description: string }> = {
  denied: {
    title: "Microfone bloqueado no navegador",
    description:
      "Libere o microfone para este site (ícone de cadeado ou de câmera na barra de endereço → Microfone → Permitir) e clique em Tentar de novo.",
  },
  notfound: {
    title: "Nenhum microfone encontrado",
    description: "Conecte o fone/headset (ou escolha outro microfone em Áudio) e clique em Tentar de novo.",
  },
  busy: {
    title: "Microfone em uso por outro programa",
    description: "Feche o programa que está usando o microfone (Teams, Zoom, gravador…) e clique em Tentar de novo.",
  },
  unsupported: {
    title: "Este navegador não acessa o microfone",
    description: "O softphone precisa de HTTPS (ou localhost) e de um navegador atualizado (Chrome ou Edge).",
  },
  error: {
    title: "Não foi possível abrir o microfone",
    description: "Verifique o dispositivo em Áudio, teste o microfone e clique em Tentar de novo.",
  },
};

type MicGuardState = {
  problem: MicProblem | null;
  checking: boolean;
  /** Último teste bem-sucedido (evita reabrir o microfone a cada transição). */
  lastOkAt: number;
};

export const useMicGuardStore = create<MicGuardState>(() => ({ problem: null, checking: false, lastOkAt: 0 }));

const sp = () => useSoftphoneStore.getState();
const guard = () => useMicGuardStore.getState();
const RECENT_OK_MS = 5000;

function setPermission(p: MicPermission) {
  if (sp().micPermission !== p) sp().patch({ micPermission: p });
}

function classify(e: unknown): MicProblem {
  switch ((e as DOMException)?.name) {
    case "NotAllowedError":
    case "SecurityError":
    case "PermissionDeniedError":
      return "denied";
    case "NotFoundError":
    case "OverconstrainedError":
    case "DevicesNotFoundError":
      return "notfound";
    case "NotReadableError":
    case "AbortError":
    case "TrackStartError":
      return "busy";
    default:
      return "error";
  }
}

/** Mesmas restrições do motor SIP para o dispositivo escolhido. */
export function micConstraints(inputId: string): MediaStreamConstraints {
  return { audio: inputId ? { deviceId: { exact: inputId } } : true, video: false };
}

let inflight: Promise<MicProblem | null> | null = null;

/**
 * Abre o microfone um instante para confirmar que funciona. Se o dispositivo escolhido sumiu, cai
 * para o padrão do sistema (e avisa). Retorna o problema encontrado, ou null.
 */
export function probeMic(): Promise<MicProblem | null> {
  if (inflight) return inflight;
  inflight = (async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermission("unsupported");
      return "unsupported" as const;
    }
    useMicGuardStore.setState({ checking: true });
    try {
      const inputId = sp().audio.inputId;
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(micConstraints(inputId));
      } catch (e) {
        if (!inputId || classify(e) !== "notfound") throw e;
        stream = await navigator.mediaDevices.getUserMedia(micConstraints(""));
        sp().setAudio({ inputId: "" });
        toast.warning("O microfone escolhido não foi encontrado; usando o padrão do sistema.");
      }
      stream.getTracks().forEach((t) => t.stop());
      setPermission("granted");
      useMicGuardStore.setState({ problem: null, lastOkAt: Date.now() });
      return null;
    } catch (e) {
      const problem = classify(e);
      if (problem === "denied") setPermission("denied");
      return problem;
    } finally {
      useMicGuardStore.setState({ checking: false });
    }
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

/** Tira o agente do ar se ele estiver "Disponível" (com chamada em curso, espera ela terminar). */
function enforce() {
  const { problem } = guard();
  const s = sp();
  if (!problem || s.ownership !== "this" || s.sessionStatus !== "ONLINE" || s.call) return;
  void goOffline(MIC_OFFLINE_REASON);
  toast.error("Sem microfone: você ficou offline e não receberá chamadas até resolver.");
}

function block(problem: MicProblem) {
  useMicGuardStore.setState({ problem });
  enforce();
}

/** Confere o microfone quando faz sentido (sem abrir o pedido de permissão sem gesto do usuário). */
async function evaluate() {
  const s = sp();
  if (s.ownership !== "this" || s.sessionStatus !== "ONLINE") return;
  if (s.micPermission === "unsupported") return block("unsupported");
  const g = guard();
  const known = s.micPermission === "granted" || s.micPermission === "denied" || g.problem != null;
  if (!known) return; // "prompt"/"unknown": o navegador pergunta no 1º atendimento
  if (s.micPermission === "granted" && !g.problem && Date.now() - g.lastOkAt < RECENT_OK_MS) return;
  const problem = await probeMic();
  if (problem) block(problem);
}

/** "Disponível" pela UI: confere o microfone antes de entrar no ar. */
export async function goAvailableChecked() {
  const problem = await probeMic();
  if (problem) {
    useMicGuardStore.setState({ problem });
    enforce();
    toast.error(`${MIC_PROBLEM_TEXT[problem].title}. Você não receberá chamadas até resolver.`);
    return;
  }
  await goAvailable();
}

/** Ação do aviso "Sem microfone": confere de novo e, se foi tirado do ar por isso, volta a "Disponível". */
export async function retryMic() {
  const s = sp();
  const wasPulled = s.sessionStatus === "OFFLINE" && s.sessionInfo === MIC_OFFLINE_REASON;
  const problem = await probeMic();
  if (problem) {
    useMicGuardStore.setState({ problem });
    toast.error(MIC_PROBLEM_TEXT[problem].title);
    return;
  }
  if (wasPulled) {
    await goAvailable();
    toast.success("Microfone funcionando. Você está disponível de novo.");
  } else {
    toast.success("Microfone funcionando.");
  }
}

/**
 * Montado uma vez no shell (MiniSoftphone): sincroniza a permissão do navegador com o store e
 * reage a status/permissão/chamada/dispositivos.
 */
export function useMicGuard() {
  // Permissions API → micPermission (antes isso só rodava com a tela de Atendimento aberta).
  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermission("unsupported");
      return;
    }
    let status: PermissionStatus | null = null;
    let disposed = false;
    const sync = () => {
      if (!status) return;
      const state = status.state as MicPermission;
      setPermission(state);
      if (state === "denied") block("denied");
      else if (state === "granted" && guard().problem === "denied") {
        useMicGuardStore.setState({ problem: null });
        if (sp().sessionStatus === "OFFLINE" && sp().sessionInfo === MIC_OFFLINE_REASON) {
          toast.info("Microfone liberado. Clique em Disponível para voltar a receber chamadas.");
        }
      }
    };
    navigator.permissions
      ?.query({ name: "microphone" as PermissionName })
      .then((s) => {
        if (disposed) return;
        status = s;
        sync();
        s.onchange = sync;
      })
      .catch(() => {});
    return () => {
      disposed = true;
      if (status) status.onchange = null;
    };
  }, []);

  // Transições: entrou em ONLINE, permissão mudou (inclusive pelo getusermediafailed do JsSIP), chamada acabou.
  useEffect(
    () =>
      useSoftphoneStore.subscribe((s, prev) => {
        if (s.ownership !== "this") return;
        const becameOnline = s.sessionStatus === "ONLINE" && prev.sessionStatus !== "ONLINE";
        const permChanged = s.micPermission !== prev.micPermission;
        if (becameOnline || (permChanged && s.sessionStatus === "ONLINE")) void evaluate();
        if (!s.call && prev.call) enforce();
      }),
    [],
  );

  // Microfone desconectado com o agente no ar.
  useEffect(() => {
    const md = navigator.mediaDevices;
    if (!md?.enumerateDevices) return;
    const onChange = async () => {
      const s = sp();
      if (s.ownership !== "this" || s.sessionStatus !== "ONLINE") return;
      try {
        const list = await md.enumerateDevices();
        if (!list.some((d) => d.kind === "audioinput")) block("notfound");
        else if (guard().problem === "notfound") void evaluate();
      } catch {
        /* ignore */
      }
    };
    md.addEventListener?.("devicechange", onChange);
    return () => md.removeEventListener?.("devicechange", onChange);
  }, []);
}
